/**
 * IT Admin → Email intake (option B, 19 Oct): what the IT HelpDesk did with
 * each email to the support mailboxes.
 *
 * - The mode (off / shadow / act) and where mail is read from.
 * - Counts of the last 7 days.
 * - The review queue (low confidence, external or unknown senders, failures)
 *   with "Create incident", "Send form link" and "Ignore"; every click is
 *   audited on the server.
 * - All emails: sender, subject, received, decision, confidence, what was
 *   done (or, in shadow, what would be done), the ticket, and on the journal
 *   provider what Ivanti did.
 * - On UAT, the test mailbox: run the sample emails or upload an `.eml`.
 *
 * Metadata only: the page never shows an email body.
 */

import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Field,
  Input,
  Select,
  StatisticBar,
  Tabs,
  Typography,
} from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { ErrorState, LoadingState, SectionCard, useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { EmailIntakeItem, EmailIntakeListResponse } from "@/lib/api";

import {
  type EmailIntakeFilter,
  useEmailIntake,
  useEmailIntakeAction,
  useEmailIntakeItem,
  useRunSamples,
  useUploadMail,
} from "./api";
import { ticketNumberOf } from "./lib";

type Status =
  | "received"
  | "ignored"
  | "reply"
  | "review"
  | "would_act"
  | "acted"
  | "failed"
  | "resolved";

const STATUS_TONE: Record<Status, "neutral" | "info" | "warning" | "success" | "danger"> = {
  received: "neutral",
  ignored: "neutral",
  reply: "info",
  review: "warning",
  would_act: "info",
  acted: "success",
  failed: "danger",
  resolved: "success",
};

function asStatus(value: string): Status {
  return value in STATUS_TONE ? (value as Status) : "received";
}

function StatusStrip({ data }: { data: EmailIntakeListResponse }) {
  const { t } = useT("tickets");
  const s = data.status;
  const sources = [
    s.graph_enabled ? t("itAdmin.emailIntake.provider.graph") : null,
    s.test_enabled ? t("itAdmin.emailIntake.provider.test") : null,
    s.journal_enabled ? t("itAdmin.emailIntake.provider.journal") : null,
  ].filter((x): x is string => x !== null);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="label">{t("itAdmin.emailIntake.modeLabel")}</Typography>
        <Badge tone={s.mode === "act" ? "success" : s.mode === "shadow" ? "info" : "neutral"}>
          {t(`itAdmin.emailIntake.mode.${s.mode}`)}
        </Badge>
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.emailIntake.providers")}:{" "}
          {sources.length > 0 ? sources.join(" · ") : t("itAdmin.emailIntake.noProvider")}
        </Typography>
      </div>
      <Typography variant="body-sm" tone="muted">
        {t("itAdmin.emailIntake.intro", { days: String(s.retention_days) })}
      </Typography>
    </div>
  );
}

function Counts({ data }: { data: EmailIntakeListResponse }) {
  const { t } = useT("tickets");
  const c = data.counts;
  return (
    <StatisticBar
      aria-label={t("itAdmin.emailIntake.counts.title", { days: String(c.days) })}
      items={[
        { label: t("itAdmin.emailIntake.counts.total"), value: String(c.total ?? 0) },
        { label: t("itAdmin.emailIntake.counts.ignored"), value: String(c.ignored ?? 0) },
        { label: t("itAdmin.emailIntake.counts.reply"), value: String(c.reply ?? 0) },
        {
          label: t("itAdmin.emailIntake.counts.acted"),
          value: String((c.acted ?? 0) + (c.would_act ?? 0)),
          tone: "accent",
        },
        {
          label: t("itAdmin.emailIntake.counts.reviewOpen"),
          value: String(c.review_open ?? 0),
          tone: (c.review_open ?? 0) > 0 ? "caution" : "neutral",
        },
      ]}
    />
  );
}

function TestMailbox({ data }: { data: EmailIntakeListResponse }) {
  const { t } = useT("tickets");
  const samples = useRunSamples();
  const upload = useUploadMail();
  const [asMe, setAsMe] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const names = data.status.samples ?? [];
  const result = samples.data ?? upload.data;
  return (
    <SectionCard
      title={t("itAdmin.emailIntake.test.title")}
      description={t("itAdmin.emailIntake.test.intro")}
    >
      <div className="flex flex-col gap-3">
        <Typography variant="caption" tone="muted">
          {names.join(" · ")}
        </Typography>
        <Checkbox.Root
          checked={asMe}
          onCheckedChange={(checked) => setAsMe(checked === true)}
          label={t("itAdmin.emailIntake.test.asMe")}
        />
        <div className="flex flex-wrap gap-2">
          {(["shadow", "act"] as const).map((mode) => (
            <Button
              key={mode}
              variant="outline"
              tone={mode === "act" ? "danger" : "neutral"}
              disabled={names.length === 0}
              loading={samples.isPending && samples.variables.mode === mode}
              onClick={() => samples.mutate({ names, as_me: asMe, mode })}
            >
              {mode === "act"
                ? t("itAdmin.emailIntake.test.runAct")
                : t("itAdmin.emailIntake.test.runShadow")}
            </Button>
          ))}
        </div>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (file !== null) {
              upload.mutate({ file, mode: null });
            }
          }}
        >
          <Field name="email-intake-upload" label={t("itAdmin.emailIntake.test.upload")}>
            <input
              type="file"
              accept=".eml,message/rfc822"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Field>
          <Button
            type="submit"
            variant="outline"
            disabled={file === null}
            loading={upload.isPending}
          >
            {t("itAdmin.emailIntake.test.run")}
          </Button>
        </form>
        {[samples.error, upload.error].map((error, i) =>
          error == null ? null : <ErrorState key={String(i)} error={error} variant="inline" />,
        )}
        {result === undefined ? null : (
          <Alert tone="success">
            {t("itAdmin.emailIntake.test.done", {
              n: String(result.items.length),
              already: String(result.already_processed ?? 0),
            })}
          </Alert>
        )}
      </div>
    </SectionCard>
  );
}

function Details({ id }: { id: number }) {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const detail = useEmailIntakeItem(id);
  if (detail.isPending) {
    return <LoadingState />;
  }
  if (detail.isError) {
    return <ErrorState error={detail.error} variant="inline" />;
  }
  return (
    <div className="bg-bg-subtle flex flex-col gap-2 rounded p-3">
      <Typography variant="label">{t("itAdmin.emailIntake.history")}</Typography>
      {(detail.data.audit ?? []).map((entry, i) => (
        <Typography key={String(i)} variant="caption">
          {formatDateTime(entry.at)} · {entry.actor} · {entry.action}
          {entry.detail ? ` · ${entry.detail}` : ""}
        </Typography>
      ))}
      {(detail.data.test_replies ?? []).map((reply, i) => (
        <div key={String(i)} className="flex flex-col gap-1">
          <Typography variant="label">{t("itAdmin.emailIntake.testReply")}</Typography>
          <Typography variant="caption" className="whitespace-pre-line">
            {reply}
          </Typography>
        </div>
      ))}
    </div>
  );
}

function Row({ item, offerings }: { item: EmailIntakeItem; offerings: string[] }) {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const act = useEmailIntakeAction();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(item.offering_name || "");
  const status = asStatus(item.status);
  const number = item.ticket_number ? ticketNumberOf(item.ticket_number) : null;
  const facts = [item.service, item.category, item.team, item.site].filter((x) => x);
  const buttons = item.actions;
  return (
    <div className="border-border flex flex-col gap-1 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="label">{item.subject || "—"}</Typography>
        <Badge size="sm" tone={STATUS_TONE[status]}>
          {t(`itAdmin.emailIntake.status.${status}`)}
        </Badge>
        {item.mode === "shadow" ? (
          <Badge size="sm" variant="outline">
            {t("itAdmin.emailIntake.mode.shadow")}
          </Badge>
        ) : null}
        {item.confidence == null ? null : (
          <Badge size="sm" variant="outline">
            {t("itAdmin.emailIntake.confidence", {
              pct: String(Math.round(item.confidence * 100)),
            })}
          </Badge>
        )}
      </div>
      <Typography variant="body-sm">
        {item.from_name ? `${item.from_name} <${item.from_address}>` : item.from_address} ·{" "}
        {formatDateTime(item.received_at)} · {item.mailbox}
        {item.position === "To" ? "" : ` (${item.position})`}
      </Typography>
      <Typography variant="body-sm">
        <strong>{item.action || "—"}</strong>
        {item.reason ? ` · ${item.reason}` : ""}
        {number !== null ? (
          <>
            {" · "}
            <Link to={`/t/${number}`} className="text-accent underline">
              {item.ticket_number}
            </Link>
          </>
        ) : item.ticket_number ? (
          ` · ${item.ticket_number}`
        ) : null}
      </Typography>
      {item.summary_en ? (
        <Typography variant="caption" tone="muted">
          {item.summary_en}
        </Typography>
      ) : null}
      {facts.length > 0 || item.ivanti_outcome ? (
        <Typography variant="caption" tone="muted">
          {facts.join(" · ")}
          {item.ivanti_outcome
            ? `${facts.length > 0 ? " · " : ""}${t("itAdmin.emailIntake.ivanti")}: ${item.ivanti_outcome}`
            : ""}
        </Typography>
      ) : null}
      {item.last_error ? (
        <Typography variant="caption" tone="danger">
          {item.last_error}
        </Typography>
      ) : null}
      <div className="flex flex-wrap items-end gap-2 pt-1">
        {buttons.create_incident ? (
          <Button
            size="sm"
            variant="outline"
            loading={act.isPending && act.variables.body.action === "create_incident"}
            onClick={() => act.mutate({ id: item.id, body: { action: "create_incident" } })}
          >
            {t("itAdmin.emailIntake.action.createIncident")}
          </Button>
        ) : null}
        {buttons.send_form_link && offerings.length > 0 ? (
          <>
            <Select.Root value={form} onValueChange={(next) => setForm(String(next ?? ""))}>
              <Select.Trigger size="sm" aria-label={t("itAdmin.emailIntake.form")}>
                <Select.Value placeholder={t("itAdmin.emailIntake.form")} />
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner sideOffset={6}>
                  <Select.Popup>
                    <Select.List>
                      {offerings.map((name) => (
                        <Select.Item key={name} value={name}>
                          <Select.ItemText>{name}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
            <Button
              size="sm"
              variant="outline"
              disabled={form === ""}
              loading={act.isPending && act.variables.body.action === "send_form_link"}
              onClick={() =>
                act.mutate({
                  id: item.id,
                  body: { action: "send_form_link", offering_name: form },
                })
              }
            >
              {t("itAdmin.emailIntake.action.sendFormLink")}
            </Button>
          </>
        ) : null}
        {buttons.ignore ? (
          <Button
            size="sm"
            variant="ghost"
            loading={act.isPending && act.variables.body.action === "ignore"}
            onClick={() => act.mutate({ id: item.id, body: { action: "ignore" } })}
          >
            {t("itAdmin.emailIntake.action.ignore")}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)}>
          {open ? t("itAdmin.emailIntake.hideDetails") : t("itAdmin.emailIntake.details")}
        </Button>
      </div>
      {act.error == null ? null : <ErrorState error={act.error} variant="inline" />}
      {open ? <Details id={item.id} /> : null}
    </div>
  );
}

function MailList({ filter, offerings }: { filter: EmailIntakeFilter; offerings: string[] }) {
  const { t } = useT("tickets");
  const [q, setQ] = useState("");
  const list = useEmailIntake(filter, q);
  return (
    <div className="flex flex-col gap-3 pt-4">
      <Field name={`email-intake-search-${filter}`} label={t("itAdmin.emailIntake.search")}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} maxLength={100} />
      </Field>
      {list.isPending ? <LoadingState /> : null}
      {list.isError ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
      {list.data !== undefined && list.data.items.length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.emailIntake.empty")}
        </Typography>
      ) : null}
      {(list.data?.items ?? []).map((item) => (
        <Row key={item.id} item={item} offerings={offerings} />
      ))}
    </div>
  );
}

export function EmailIntakePage() {
  const { t } = useT("tickets");
  const overview = useEmailIntake("open_review", "");
  const [tab, setTab] = useState<EmailIntakeFilter>("open_review");
  if (overview.isPending) {
    return <LoadingState />;
  }
  if (overview.isError) {
    return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  }
  const data = overview.data;
  const offerings = data.status.offerings ?? [];
  return (
    <div className="flex flex-col gap-6">
      <StatusStrip data={data} />
      {data.status.mode === "off" ? (
        <Alert tone="neutral">{t("itAdmin.emailIntake.off")}</Alert>
      ) : (
        <>
          <Counts data={data} />
          {data.status.test_enabled ? <TestMailbox data={data} /> : null}
          <Tabs.Root value={tab} onValueChange={(value) => setTab(value as EmailIntakeFilter)}>
            <Tabs.List aria-label={t("itAdmin.section.emailIntake")}>
              <Tabs.Tab value="open_review">
                {t("itAdmin.emailIntake.tab.review", {
                  n: String(data.counts.review_open ?? 0),
                })}
              </Tabs.Tab>
              <Tabs.Tab value="all">{t("itAdmin.emailIntake.tab.all")}</Tabs.Tab>
              <Tabs.Indicator />
            </Tabs.List>
            <Tabs.Panel value="open_review">
              <MailList filter="open_review" offerings={offerings} />
            </Tabs.Panel>
            <Tabs.Panel value="all">
              <MailList filter="all" offerings={offerings} />
            </Tabs.Panel>
          </Tabs.Root>
        </>
      )}
    </div>
  );
}
